/**
 * ModuleHub Page - Redesigned
 *
 * Central dashboard for managing which modules/pages are enabled in the application.
 * Modern glass-morphism design with unified styling.
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { Icons } from '../components/Icons';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  usePageModules,
  type ModuleCategory,
  type PageModule,
} from '../hooks/usePageModules';
import type { UserMode } from '../hooks/useUserMode';

interface ModuleHubProps {
  userMode?: UserMode;
  onNavigate?: (pageId: string) => void;
  isDarkMode?: boolean;
  onToggleDarkMode?: () => void;
  onToggleUserMode?: () => void;
}

// Icon mapping
const IconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Briefcase: Icons.Briefcase,
  Shield: Icons.Shield,
  Settings: Icons.Settings,
  BarChart: Icons.BarChart,
  PieChart: Icons.PieChart,
  Activity: Icons.Activity,
  TrendingUp: Icons.TrendingUp,
  TrendingDown: Icons.TrendingDown,
  Server: Icons.Server,
  Hash: Icons.Hash,
  Wallet: Icons.Wallet,
  CryptoCom: Icons.CryptoCom,
  KuCoin: Icons.KuCoin,
  BookOpen: Icons.BookOpen,
  FileText: Icons.FileText,
  Bot: Icons.Bot,
  Sparkles: Icons.Sparkles,
  Target: Icons.Target,
  ArrowRightLeft: Icons.ArrowRightLeft,
  // Official monitor icons
  Flux: Icons.Flux,
  Cronos: Icons.Cronos,
  Hedera: Icons.Hedera,
  TheGraph: Icons.TheGraph,
};

// Category config
const CATEGORY_CONFIG: Record<ModuleCategory, { color: string; gradient: string; icon: keyof typeof Icons }> = {
  core: { color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', icon: 'Star' },
  bitcoin: { color: '#f7931a', gradient: 'linear-gradient(135deg, #f7931a 0%, #e88a0c 100%)', icon: 'Bitcoin' },
  analytics: { color: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', icon: 'BarChart' },
  monitoring: { color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', icon: 'Activity' },
  exchanges: { color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', icon: 'Wallet' },
  tools: { color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', icon: 'Settings' },
  community: { color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', icon: 'Heart' },
  advanced: { color: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', icon: 'Zap' },
};

// Module completion percentages
const MODULE_COMPLETION: Record<string, number> = {
  'module-hub': 100, 'portfolio': 100, 'seed-vault': 100, 'settings': 100,
  'analytics': 100,
  'bitcoin-cycles': 100, 'dashboard-ipad': 100, 'fluxnode-monitor': 100,
  'hedera-monitor': 100, 'cronos-monitor': 100, 'thegraph-monitor': 100, 'icp-monitor': 100,
  'wallet-tracker': 60, 'cryptocom-dashboard': 60,
  'kucoin-dashboard': 20, 'academy': 10, 'guide': 95, 'tax-center': 85,
  'changelog': 90, 'bot': 90, 'autopilot': 80,
  'portfolio-rebalancing': 60, 'logs': 90,
  'bitcoin-retirement': 100,
};

export function ModuleHub({
  userMode = 'simple',
  onNavigate,
  isDarkMode = false,
  onToggleDarkMode,
  onToggleUserMode,
}: ModuleHubProps) {
  const { t } = useTranslation();
   
  const { language: _lang } = useLanguage();
  const isMobile = useIsMobile(768);

  const {
    isModuleEnabled,
    toggleModule,
    enableCategory,
    disableCategory,
    resetToDefaults,
    enabledCount,
    totalOptionalModules,
    getModulesByCategory,
  } = usePageModules();

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<ModuleCategory[]>([
    'core', 'bitcoin', 'analytics', 'monitoring', 'exchanges', 'tools', 'community'
  ]);

  const modulesByCategory = getModulesByCategory();

  // Filter modules by search
  const filteredModulesByCategory = useMemo(() => {
    if (!searchQuery.trim()) return modulesByCategory;

    const query = searchQuery.toLowerCase();
    const filtered: Record<ModuleCategory, PageModule[]> = {} as Record<ModuleCategory, PageModule[]>;

    Object.entries(modulesByCategory).forEach(([cat, modules]) => {
      filtered[cat as ModuleCategory] = modules.filter(m => {
        const label = t(`moduleHub.modules.${getModuleKey(m.id)}.label`).toLowerCase();
        const desc = t(`moduleHub.modules.${getModuleKey(m.id)}.description`).toLowerCase();
        return label.includes(query) || desc.includes(query) || m.id.includes(query);
      });
    });

    return filtered;
  }, [modulesByCategory, searchQuery, t]);

  const getModuleKey = (moduleId: string): string => {
    const keyMap: Record<string, string> = {
      'module-hub': 'moduleHub', 'portfolio': 'portfolio', 'seed-vault': 'seedVault',
      'settings': 'settings', 'analytics': 'analytics',
      'bitcoin-cycles': 'bitcoinCycles', 'fluxnode-monitor': 'fluxnodeMonitor',
      'hedera-monitor': 'hederaMonitor', 'cronos-monitor': 'cronosMonitor',
      'thegraph-monitor': 'thegraphMonitor', 'algorand-monitor': 'algorandMonitor',
      'cardano-monitor': 'cardanoMonitor', 'xdc-monitor': 'xdcMonitor',
      'vechain-monitor': 'vechainMonitor', 'icp-monitor': 'icpMonitor',
      'axelar-monitor': 'axelarMonitor', 'wallet-tracker': 'walletTracker',
      'cryptocom-dashboard': 'cryptocomDashboard', 'kucoin-dashboard': 'kucoinDashboard',
      'academy': 'academy', 'guide': 'guide', 'tax-center': 'taxCenter',
      'dashboard-ipad': 'dashboardIpad', 'changelog': 'changelog',
      'bot': 'bot', 'autopilot': 'autopilot', 'portfolio-rebalancing': 'portfolioRebalancing',
      'logs': 'logs',
      'portfolio-onchain': 'portfolioOnchain', 'crypto-quiz': 'cryptoQuiz',
      'swap-analytics': 'swapAnalytics', 'ai-consensus': 'aiConsensus',
      'alerts': 'alerts', 'pricing': 'pricing',
      'bitcoin-retirement': 'bitcoinRetirement', 'congress-trading': 'congressTrading',
    };
    return keyMap[moduleId] || moduleId;
  };

  const toggleCategory = (category: ModuleCategory) => {
    setExpandedCategories(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const getCategoryEnabledCount = (category: ModuleCategory): number => {
    return modulesByCategory[category].filter(m => isModuleEnabled(m.id) && !m.alwaysVisible).length;
  };

  const getCategoryTotalCount = (category: ModuleCategory): number => {
    return modulesByCategory[category].filter(m => !m.alwaysVisible).length;
  };

  // Styles
  const s = {
    container: {
      paddingBottom: '40px',
    } as React.CSSProperties,
    header: {
      background: 'var(--card-bg)',
      borderRadius: '16px',
      border: '1px solid var(--border)',
      padding: isMobile ? '20px' : '24px',
      marginBottom: '24px',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    } as React.CSSProperties,
    headerTop: {
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'space-between',
      flexDirection: isMobile ? 'column' : 'row',
      gap: '16px',
      marginBottom: '20px',
    } as React.CSSProperties,
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
    } as React.CSSProperties,
    headerIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '14px',
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
    } as React.CSSProperties,
    title: {
      fontSize: isMobile ? '22px' : '26px',
      fontWeight: 700,
      color: 'var(--text)',
      margin: 0,
    } as React.CSSProperties,
    subtitle: {
      fontSize: '13px',
      color: 'var(--muted)',
      marginTop: '4px',
    } as React.CSSProperties,
    statsRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    } as React.CSSProperties,
    statBadge: {
      padding: '8px 16px',
      borderRadius: '20px',
      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)',
      border: '1px solid rgba(245, 158, 11, 0.3)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    } as React.CSSProperties,
    statNumber: {
      fontSize: '18px',
      fontWeight: 700,
      color: '#f59e0b',
    } as React.CSSProperties,
    statLabel: {
      fontSize: '12px',
      color: 'var(--muted)',
    } as React.CSSProperties,
    actionsBar: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '10px',
      paddingTop: '16px',
      borderTop: '1px solid var(--border)',
    } as React.CSSProperties,
    actionButton: (isActive: boolean, color: string) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      fontSize: '13px',
      fontWeight: 600,
      background: isActive ? color : 'rgba(128, 128, 128, 0.08)',
      color: isActive ? 'white' : 'var(--text)',
      border: '1px solid ' + (isActive ? 'transparent' : 'var(--border)'),
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    }) as React.CSSProperties,
    searchContainer: {
      flex: 1,
      minWidth: '200px',
      maxWidth: '300px',
      position: 'relative',
    } as React.CSSProperties,
    searchInput: {
      width: '100%',
      padding: '10px 14px 10px 38px',
      fontSize: '13px',
      background: 'rgba(128, 128, 128, 0.08)',
      border: '1px solid var(--border)',
      borderRadius: '10px',
      color: 'var(--text)',
      outline: 'none',
    } as React.CSSProperties,
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: 'var(--muted)',
      display: 'flex',
    } as React.CSSProperties,
    categorySection: {
      marginBottom: '24px',
    } as React.CSSProperties,
    categoryHeader: (isCore: boolean) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '14px 18px',
      background: isCore
        ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)'
        : 'var(--card-bg)',
      border: isCore ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid var(--border)',
      borderRadius: '12px',
      cursor: 'pointer',
      marginBottom: '12px',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      transition: 'all 0.2s ease',
    }) as React.CSSProperties,
    categoryLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    } as React.CSSProperties,
    categoryIcon: (gradient: string) => ({
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      background: gradient,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    }) as React.CSSProperties,
    categoryTitle: {
      fontSize: '15px',
      fontWeight: 600,
      color: 'var(--text)',
    } as React.CSSProperties,
    categoryDesc: {
      fontSize: '12px',
      color: 'var(--muted)',
      marginTop: '2px',
    } as React.CSSProperties,
    categoryRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    } as React.CSSProperties,
    categoryCount: {
      padding: '4px 10px',
      borderRadius: '12px',
      background: 'rgba(128, 128, 128, 0.1)',
      fontSize: '12px',
      fontWeight: 600,
      color: 'var(--muted)',
    } as React.CSSProperties,
    toggleAllBtn: (allEnabled: boolean) => ({
      padding: '6px 12px',
      fontSize: '11px',
      fontWeight: 600,
      background: allEnabled ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
      color: allEnabled ? '#ef4444' : '#10b981',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
    }) as React.CSSProperties,
    modulesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '12px',
    } as React.CSSProperties,
    moduleCard: (isEnabled: boolean, isLocked: boolean, categoryColor: string) => ({
      padding: '16px',
      background: isLocked
        ? `linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.06) 100%)`
        : isEnabled
          ? `linear-gradient(135deg, ${categoryColor}20 0%, ${categoryColor}10 100%)`
          : `linear-gradient(135deg, ${categoryColor}08 0%, ${categoryColor}04 100%)`,
      border: isLocked
        ? '1px solid rgba(245, 158, 11, 0.4)'
        : isEnabled
          ? `1px solid ${categoryColor}40`
          : `1px dashed ${categoryColor}30`,
      borderRadius: '14px',
      cursor: isLocked ? 'default' : 'pointer',
      transition: 'all 0.25s ease',
      position: 'relative',
      overflow: 'hidden',
      opacity: isEnabled || isLocked ? 1 : 0.75,
      backdropFilter: 'blur(8px)',
    }) as React.CSSProperties,
    moduleHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: '12px',
    } as React.CSSProperties,
    moduleIconWrapper: {
      position: 'relative',
    } as React.CSSProperties,
    moduleIcon: (isEnabled: boolean, isLocked: boolean, color: string) => ({
      width: '42px',
      height: '42px',
      borderRadius: '12px',
      background: isLocked
        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
        : isEnabled
          ? `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`
          : `${color}18`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: isEnabled || isLocked ? 'white' : color,
      transition: 'all 0.25s ease',
      boxShadow: isEnabled && !isLocked ? `0 4px 12px ${color}30` : 'none',
    }) as React.CSSProperties,
    completeBadge: {
      position: 'absolute',
      top: '-4px',
      right: '-4px',
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      background: '#10b981',
      border: '2px solid var(--card-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    } as React.CSSProperties,
    toggle: (isEnabled: boolean, color: string, mobile: boolean = false) => ({
      width: mobile ? '40px' : '48px',
      height: mobile ? '22px' : '26px',
      borderRadius: mobile ? '11px' : '13px',
      border: 'none',
      background: isEnabled ? `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)` : 'rgba(128, 128, 128, 0.2)',
      cursor: 'pointer',
      position: 'relative',
      transition: 'all 0.25s ease',
      flexShrink: 0,
      boxShadow: isEnabled ? `0 2px 8px ${color}40` : 'none',
    }) as React.CSSProperties,
    toggleKnob: (isEnabled: boolean, mobile: boolean = false) => ({
      position: 'absolute',
      top: mobile ? '2px' : '3px',
      left: isEnabled ? (mobile ? '20px' : '25px') : (mobile ? '2px' : '3px'),
      width: mobile ? '18px' : '20px',
      height: mobile ? '18px' : '20px',
      borderRadius: '50%',
      background: 'white',
      transition: 'left 0.25s ease',
      boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
    }) as React.CSSProperties,
    addIcon: (color: string) => ({
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      background: `${color}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: color,
      flexShrink: 0,
    }) as React.CSSProperties,
    lockedBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      fontSize: '10px',
      fontWeight: 700,
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
      borderRadius: '6px',
      textTransform: 'uppercase' as const,
    } as React.CSSProperties,
    expertBadge: {
      padding: '4px 8px',
      fontSize: '10px',
      fontWeight: 600,
      background: 'rgba(139, 92, 246, 0.15)',
      color: '#8b5cf6',
      borderRadius: '6px',
      textTransform: 'uppercase' as const,
    } as React.CSSProperties,
    authBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '3px',
      padding: '3px 6px',
      fontSize: '9px',
      fontWeight: 600,
      background: 'rgba(59, 130, 246, 0.15)',
      color: '#3b82f6',
      borderRadius: '4px',
      marginLeft: 'auto',
    } as React.CSSProperties,
    moduleName: {
      fontSize: '14px',
      fontWeight: 600,
      color: 'var(--text)',
      marginBottom: '4px',
    } as React.CSSProperties,
    moduleDesc: {
      fontSize: '12px',
      color: 'var(--muted)',
      lineHeight: 1.4,
      marginBottom: '10px',
    } as React.CSSProperties,
    progressBar: {
      height: '4px',
      background: 'var(--border)',
      borderRadius: '2px',
      overflow: 'hidden',
      marginBottom: '10px',
    } as React.CSSProperties,
    progressFill: (completion: number) => ({
      height: '100%',
      width: `${completion}%`,
      background: completion >= 80 ? '#3b82f6' : completion >= 50 ? '#f59e0b' : '#ef4444',
      borderRadius: '2px',
      transition: 'width 0.3s ease',
    }) as React.CSSProperties,
    progressLabel: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '10px',
      marginBottom: '4px',
    } as React.CSSProperties,
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: 'var(--muted)',
    } as React.CSSProperties,
  };

  const renderModuleCard = (module: PageModule, categoryColor: string) => {
    const IconComponent = IconMap[module.icon] || Icons.Package;
    const isEnabled = isModuleEnabled(module.id);
    const isLocked = !!module.alwaysVisible;
    const isExpertOnly = module.expertOnly && userMode !== 'expert';
    const completion = MODULE_COMPLETION[module.id] ?? 0;

    if (isExpertOnly) return null;

    return (
      <div
        key={module.id}
        style={s.moduleCard(isEnabled, isLocked, categoryColor)}
        onClick={() => {
          if (!isLocked && !isExpertOnly) {
            toggleModule(module.id);
          }
        }}
        onMouseEnter={e => {
          if (!isLocked) {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.boxShadow = `0 8px 24px ${categoryColor}20`;
            e.currentTarget.style.borderColor = isEnabled ? `${categoryColor}60` : `${categoryColor}50`;
          }
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.opacity = isEnabled || isLocked ? '1' : '0.75';
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.borderColor = isEnabled ? `${categoryColor}40` : `${categoryColor}30`;
        }}
      >
        {/* Header */}
        <div style={s.moduleHeader}>
          <div style={s.moduleIconWrapper as React.CSSProperties}>
            <div style={s.moduleIcon(isEnabled, isLocked, categoryColor)}>
              <IconComponent size={20} />
            </div>
            {completion === 100 && (
              <div style={s.completeBadge as React.CSSProperties}>
                <Icons.Check size={10} className="text-white" />
              </div>
            )}
          </div>

          {isLocked ? (
            <div style={s.lockedBadge}>
              <Icons.Lock size={10} />
              {t('moduleHub.essential')}
            </div>
          ) : isExpertOnly ? (
            <div style={s.expertBadge}>{t('moduleHub.expert')}</div>
          ) : isEnabled ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleModule(module.id);
              }}
              style={s.toggle(isEnabled, categoryColor, isMobile) as React.CSSProperties}
            >
              <span style={s.toggleKnob(isEnabled, isMobile) as React.CSSProperties} />
            </button>
          ) : (
            <div style={s.addIcon(categoryColor) as React.CSSProperties}>
              <Icons.Plus size={16} />
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={s.moduleName}>
            {t(`moduleHub.modules.${getModuleKey(module.id)}.label`)}
          </div>
          {module.requiresAuth && (
            <div style={s.authBadge} title={t('moduleHub.requiresLogin', 'Connexion requise')}>
              <Icons.User size={10} />
            </div>
          )}
        </div>
        <div style={{
          ...s.moduleDesc,
          opacity: isEnabled || isLocked ? 1 : 0.7,
        }}>
          {t(`moduleHub.modules.${getModuleKey(module.id)}.description`)}
        </div>

        {/* Progress Bar */}
        {completion < 100 && (isEnabled || isLocked) && (
          <>
            <div style={s.progressLabel}>
              <span style={{ color: 'var(--muted)' }}>En développement</span>
              <span style={{
                fontWeight: 600,
                color: completion >= 80 ? categoryColor : completion >= 50 ? '#f59e0b' : '#ef4444'
              }}>
                {completion}%
              </span>
            </div>
            <div style={s.progressBar}>
              <div style={{
                ...s.progressFill(completion),
                background: completion >= 80
                  ? `linear-gradient(90deg, ${categoryColor}, ${categoryColor}aa)`
                  : completion >= 50
                    ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                    : 'linear-gradient(90deg, #ef4444, #f87171)',
              }} />
            </div>
          </>
        )}

        {/* Open Button or Enable hint */}
        {(isEnabled || isLocked) && onNavigate ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(module.id === 'settings' ? 'settings' : module.id);
            }}
            style={{
              fontSize: '11px',
              color: categoryColor,
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
            }}
          >
            {t('moduleHub.open')}
            <Icons.ChevronRight size={12} />
          </div>
        ) : !isLocked && (
          <div style={{
            fontSize: '11px',
            color: categoryColor,
            opacity: 0.7,
            marginTop: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <Icons.Plus size={12} />
            {t('moduleHub.clickToEnable', 'Cliquer pour activer')}
          </div>
        )}
      </div>
    );
  };

  const renderCategorySection = (category: ModuleCategory) => {
    const modules = filteredModulesByCategory[category];
    const filteredModules = modules
      .filter(m => m.id !== 'swap-analytics')
      .filter(m => m.id !== 'pricing') // Accessible only via dropdown when connected
      .filter(m => userMode === 'expert' || !m.expertOnly);

    if (filteredModules.length === 0) return null;

    const isExpanded = expandedCategories.includes(category);
    const enabledInCategory = getCategoryEnabledCount(category);
    const totalInCategory = getCategoryTotalCount(category);
    const allEnabled = enabledInCategory === totalInCategory && totalInCategory > 0;
    const isCore = category === 'core';
    const config = CATEGORY_CONFIG[category];
    const CategoryIcon = Icons[config.icon] || Icons.Package;

    return (
      <div key={category} style={s.categorySection}>
        <div
          style={s.categoryHeader(isCore)}
          onClick={() => toggleCategory(category)}
          onMouseEnter={e => {
            if (!isCore) e.currentTarget.style.background = 'rgba(128, 128, 128, 0.06)';
          }}
          onMouseLeave={e => {
            if (!isCore) e.currentTarget.style.background = 'var(--card-bg)';
          }}
        >
          <div style={s.categoryLeft}>
            <div style={s.categoryIcon(config.gradient)}>
              <CategoryIcon size={18} />
            </div>
            <div>
              <div style={s.categoryTitle}>{t(`moduleHub.categories.${category}`)}</div>
              <div style={s.categoryDesc}>{t(`moduleHub.categoryDescriptions.${category}`)}</div>
            </div>
          </div>

          <div style={s.categoryRight}>
            {!isCore && totalInCategory > 0 && (
              <>
                <div style={s.categoryCount}>{enabledInCategory}/{totalInCategory}</div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (allEnabled) disableCategory(category);
                    else enableCategory(category);
                  }}
                  style={s.toggleAllBtn(allEnabled)}
                >
                  {allEnabled ? t('moduleHub.disableAll') : t('moduleHub.enableAll')}
                </button>
              </>
            )}
            {isCore && (
              <span style={{ fontSize: '11px', color: '#b45309', fontWeight: 600 }}>
                {t('moduleHub.alwaysActive')}
              </span>
            )}
            <span style={{
              display: 'flex',
              transition: 'transform 0.2s',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              color: isCore ? '#b45309' : 'var(--muted)',
            }}>
              <Icons.ChevronDown size={18} />
            </span>
          </div>
        </div>

        {isExpanded && (
          <div style={s.modulesGrid}>
            {filteredModules.map(m => renderModuleCard(m, config.color))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerTop as React.CSSProperties}>
          <div style={s.headerTitle}>
            <div style={s.headerIcon}>
              <Icons.Package size={24} />
            </div>
            <div>
              <h1 style={s.title}>{t('moduleHub.title')}</h1>
              <p style={s.subtitle}>{t('moduleHub.subtitle')}</p>
            </div>
          </div>

          <div style={s.statsRow}>
            <div style={s.statBadge}>
              <span style={s.statNumber}>{enabledCount}</span>
              <span style={s.statLabel}>/ {totalOptionalModules} {t('moduleHub.modulesActive')}</span>
            </div>
            <button
              onClick={resetToDefaults}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                fontSize: '12px',
                fontWeight: 600,
                background: 'rgba(128, 128, 128, 0.08)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              <Icons.RotateCcw size={14} />
              {t('moduleHub.reset')}
            </button>
          </div>
        </div>

        {/* Actions Bar */}
        <div style={s.actionsBar as React.CSSProperties}>
          {/* Search */}
          <div style={s.searchContainer as React.CSSProperties}>
            <span style={s.searchIcon as React.CSSProperties}><Icons.Search size={16} /></span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('moduleHub.searchModules', 'Rechercher un module...')}
              style={s.searchInput}
            />
          </div>

          {/* Dark Mode */}
          {onToggleDarkMode && (
            <button
              onClick={onToggleDarkMode}
              style={s.actionButton(isDarkMode, 'linear-gradient(135deg, #1e293b 0%, #334155 100%)')}
            >
              {isDarkMode ? <Icons.Moon size={16} /> : <Icons.Sun size={16} />}
              {isDarkMode ? t('moduleHub.darkMode') : t('moduleHub.lightMode')}
            </button>
          )}

          {/* User Mode */}
          {onToggleUserMode && (
            <button
              onClick={onToggleUserMode}
              style={s.actionButton(userMode === 'expert', 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)')}
            >
              {userMode === 'expert' ? <Icons.Zap size={16} /> : <Icons.User size={16} />}
              {userMode === 'expert' ? t('moduleHub.expertMode') : t('moduleHub.simpleMode')}
            </button>
          )}

          {/* Guide */}
          {onNavigate && (
            <button
              onClick={() => onNavigate('guide')}
              style={s.actionButton(false, '')}
            >
              <Icons.BookOpen size={16} />
              {t('moduleHub.completeGuide')}
            </button>
          )}
        </div>
      </div>

      {/* Categories */}
      {searchQuery && Object.values(filteredModulesByCategory).every(arr => arr.length === 0) ? (
        <div style={s.emptyState as React.CSSProperties}>
          <span style={{ opacity: 0.3, marginBottom: '12px', display: 'block' }}><Icons.Search size={40} /></span>
          <p>{t('moduleHub.noResults', 'Aucun module trouvé')}</p>
        </div>
      ) : (
        <>
          {renderCategorySection('core')}
          {renderCategorySection('bitcoin')}
          {renderCategorySection('analytics')}
          {renderCategorySection('monitoring')}
          {renderCategorySection('tools')}
          {renderCategorySection('community')}
          {renderCategorySection('exchanges')}
          {userMode === 'expert' && renderCategorySection('advanced')}
        </>
      )}
    </div>
  );
}
